import axios, { AxiosInstance, type AxiosResponse } from 'axios';
import { handleExpiredAdminSession } from '@/utils/sessionLogout';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const NETWORK_ERROR_MESSAGE = 'Network error. Please try again.';
const VALIDATION_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const TECHNICAL_MESSAGE_PATTERNS = [
  /\b(?:sk|pk|rk|acct)_[A-Za-z0-9]+\b/i,
  /\bapi[- ]?key\b/i,
  /\bsecret\b/i,
  /\baccess token\b/i,
  /\brefresh token\b/i,
  /\bstripe\b/i,
  /\brequest id\b/i,
  /\bwebhook\b/i,
  /\btrace\b/i,
  /\bstack\b/i,
  /\bexception\b/i,
  /\bdatabase\b/i,
  /\bmongo(?:db|ose)?\b/i,
  /\bprisma\b/i,
  /\bsql\b/i,
  /\bundefined\b/i,
  /\bnull\b/i,
  /\bcannot read properties\b/i,
  /\bprovided key\b/i,
  /\baccess may have been revoked\b/i,
  /\bdoes not have access to account\b/i,
  /\bECONN(?:REFUSED|RESET)?\b/i,
  /\bETIMEDOUT\b/i,
  /\bENOTFOUND\b/i,
  /\bEPIPE\b/i,
  /\bsocket\b/i,
];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeMessage(message: unknown): string | null {
  if (typeof message !== 'string') return null;
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isTechnicalMessage(message: string) {
  return (
    TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message)) ||
    message.length > 180 ||
    /https?:\/\//i.test(message) ||
    /\n\s*at\s+/i.test(message)
  );
}

function isSafeUserFacingMessage(message: string) {
  return !isTechnicalMessage(message);
}

function getFallbackMessage(status?: number) {
  if (status === 400 || status === 422) return VALIDATION_ERROR_MESSAGE;
  if (status === 401 || status === 403 || status === 404) return NETWORK_ERROR_MESSAGE;
  return GENERIC_ERROR_MESSAGE;
}

function sanitizeMessageValue(message: unknown, status?: number): string {
  if (Array.isArray(message)) {
    const safeMessages = message
      .map((item) => normalizeMessage(item))
      .filter((item): item is string => Boolean(item))
      .filter((item) => isSafeUserFacingMessage(item));

    if (safeMessages.length > 0) {
      return safeMessages.join(', ');
    }

    return getFallbackMessage(status);
  }

  const normalized = normalizeMessage(message);
  if (!normalized) {
    return getFallbackMessage(status);
  }

  if (status === 401 && /invalid credentials|incorrect email or password/i.test(normalized)) {
    return NETWORK_ERROR_MESSAGE;
  }

  if (isSafeUserFacingMessage(normalized)) {
    return normalized;
  }

  return getFallbackMessage(status);
}

export function sanitizeAxiosError(error: unknown) {
  if (!axios.isAxiosError(error)) {
    return error;
  }

  const response = error.response;
  const status = response?.status;
  const sanitizedMessage = response
    ? sanitizeMessageValue(
        isPlainObject(response.data) ? response.data.message : response.data,
        status,
      )
    : NETWORK_ERROR_MESSAGE;

  const responseData =
    response && isPlainObject(response.data)
      ? response.data
      : {};

  if (response) {
    (response as AxiosResponse & { data: Record<string, unknown> }).data = {
      ...responseData,
      message: sanitizedMessage,
    };
  } else {
    const fallbackConfig = (error.config ?? { headers: {} }) as AxiosResponse['config'];
    (error as typeof error & { response: AxiosResponse & { data: Record<string, unknown> } }).response = {
      config: fallbackConfig,
      data: { message: sanitizedMessage },
      headers: {},
      status: 0,
      statusText: '',
    };
  }

  error.message = sanitizedMessage;

  return error;
}

export function attachSanitizedErrorInterceptor(client: AxiosInstance) {
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const sanitizedError = sanitizeAxiosError(error);
      const status = axios.isAxiosError(sanitizedError)
        ? sanitizedError.response?.status
        : undefined;

      if (status === 401) {
        handleExpiredAdminSession();
      }

      return Promise.reject(sanitizedError);
    },
  );

  return client;
}

export function createSanitizedJsonApi(baseURL?: string) {
  const client = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
  });

  return attachSanitizedErrorInterceptor(client);
}
